import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { RootState } from "./store";
import { axiosApi, downloadFile } from "../helpers";
import { ENDPOINTS_TTS, URL_TTS } from "../constants/api_endpoints";
import { IImportDictionaryProps, INormalizeResponse, IPhonemesResponse, IPutDictionaryProps, IRevoiceProps, ISynthesizeSpeechProps, ITtsState } from "../types/ttsTypes";
import { IResponse, RequestStatus, ResponseStatus } from "../types/statusTypes";

const initialState: ITtsState = {
	synthesis: {
		dataUrl: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	dictionary: {
		data: [],
		status: RequestStatus.IDLE,
	},
	dictionaryImport: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	dictionaryExport: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	dictionaryPut: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	normalization: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		text: null,
	},
	phonemes: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		text: null,
	},
	revoicersList: {
		data: [],
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	revoice: {
		dataUrl: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// синтез речи
export const synthesizeSpeech = createAsyncThunk(
	"tts/synthesizeSpeech",
	async ({ modelName, text, rate, pitch, volume, frequency, format }: ISynthesizeSpeechProps, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<Blob | IResponse> = await axiosApi.post(
				`${URL_TTS}/${ENDPOINTS_TTS.SYNTHESIZE}/${modelName}`,
				{
					text,
					rate,
					pitch,
					volume,
					frequency,
					format,
				},
				{
					responseType: "blob",
				}
			);
			return response.data;
		} catch (error) {
			if (error) {
				// возвращаем данные ошибки, пришедшие с бэка
				return rejectWithValue(error);
			}
		}
	}
);

// получение словаря ударений
export const getDictionary = createAsyncThunk("tts/getDictionary", async (): Promise<[string, string][]> => {
	const response: AxiosResponse<[string, string][]> = await axiosApi.get(`${URL_TTS}/${ENDPOINTS_TTS.DICTIONARY_GET}`);
	return response.data;
});

// экспорт словаря ударений (.csv файл)
export const exportDictionary = createAsyncThunk("tts/exportDictionary", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<Blob | IResponse> = await axiosApi.get(`${URL_TTS}/${ENDPOINTS_TTS.DICTIONARY_EXPORT}`, {
			responseType: "blob",
		});
		if (response.data instanceof Blob) {
			downloadFile(response.data, "stressDictionary.csv");
		} else return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// импорт словаря ударений
export const importDictionary = createAsyncThunk("tts/importDictionary", async ({ formData }: IImportDictionaryProps): Promise<IResponse> => {
	const response: AxiosResponse<IResponse> = await axiosApi.post(
		`${URL_TTS}/${ENDPOINTS_TTS.DICTIONARY_IMPORT}`,
		{ csv: formData.get("file") },
		{
			headers: {
				"Content-Type": "multipart/form-data",
			},
		}
	);
	return response.data;
});

// изменение словаря ударений
export const putDictionary = createAsyncThunk("tts/putDictionary", async ({ text }: IPutDictionaryProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_TTS}/${ENDPOINTS_TTS.DICTIONARY_PUT}`, {
			json: JSON.stringify(text),
		});
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// нормализация
export const normalizeText = createAsyncThunk("tts/normalize", async (text: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<INormalizeResponse> = await axiosApi.post(`${URL_TTS}/${ENDPOINTS_TTS.NORMALIZE}`, {
			text,
		});
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// преобразование в фонемы
export const convertTextToPhonemes = createAsyncThunk("tts/phonemes", async (text: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IPhonemesResponse> = await axiosApi.post(`${URL_TTS}/${ENDPOINTS_TTS.PHONEMES}`, {
			text,
		});
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// получение списка моделей искажения
export const getRevoicersList = createAsyncThunk("tts/revoicersList", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<string[] | IResponse> = await axiosApi.get(`${URL_TTS}/${ENDPOINTS_TTS.REVOICERS_LIST}`);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// искажение голоса
export const revoice = createAsyncThunk("tts/revoice", async ({ revoicer, formData }: IRevoiceProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<Blob | IResponse> = await axiosApi.post(
			`${URL_TTS}/${ENDPOINTS_TTS.REVOICE}/${revoicer}`,
			{
				wav: formData.get("file"),
			},
			{
				headers: {
					"Content-Type": "multipart/form-data",
				},
				responseType: "blob",
			}
		);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

const ttsSlice = createSlice({
	name: "tts",
	initialState,
	reducers: {
		// изменение ячейки таблицы словаря ударений (по факту изменение строки полностью)
		editDictionaryDataCell: (state, action: PayloadAction<{ index: number; row: string[] }>) => {
			state.dictionary.data.splice(action.payload.index, 1, action.payload.row);
		},
		// добавление строки в словарь ударений
		addRow: (state, action: PayloadAction<string[]>) => {
			state.dictionary.data.push(action.payload);
		},
		// удаление строк из словаря ударений
		deleteRows: (state, action: PayloadAction<number[]>) => {
			state.dictionary.data = state.dictionary.data.filter((_, idx) => !action.payload.includes(idx));
		},
		// очистка state
		clearState: (state) => {
			state.synthesis = initialState.synthesis;
			state.dictionary = initialState.dictionary;
			state.dictionaryExport = initialState.dictionaryExport;
			state.dictionaryImport = initialState.dictionaryImport;
			state.dictionaryPut = initialState.dictionaryPut;
			state.normalization = initialState.normalization;
			state.phonemes = initialState.phonemes;
			state.revoicersList = initialState.revoicersList;
			state.revoice = initialState.revoice;
		},
		// очистка результата синтеза речи
		clearSynthesisData: (state) => {
			state.synthesis = initialState.synthesis;
		},
		// очистка словаря ударений
		clearDictionary: (state) => {
			state.dictionary = initialState.dictionary;
		},
		// очистка статуса экспорта словаря ударений
		clearExportDictionary: (state) => {
			state.dictionaryExport = initialState.dictionaryExport;
		},
		// очистка статуса импорта словаря ударений
		clearImportDictionary: (state) => {
			state.dictionaryImport = initialState.dictionaryImport;
		},
		// очистка статуса изменения словаря ударений
		clearPutDictionary: (state) => {
			state.dictionaryPut = initialState.dictionaryPut;
		},
		// очистка данных нормализации
		clearNormalization: (state) => {
			state.normalization = initialState.normalization;
		},
		// очистка данных перевода текста в фонемы
		clearPhonemes: (state) => {
			state.phonemes = initialState.phonemes;
		},
		// очистка списка искажений
		clearRevoicersList: (state) => {
			state.revoicersList = initialState.revoicersList;
		},
		// очистка результата искажения голоса
		clearRevoice: (state) => {
			state.revoice = initialState.revoice;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(synthesizeSpeech.pending, (state) => {
				state.synthesis.status = RequestStatus.LOADING;
			})
			.addCase(synthesizeSpeech.fulfilled, (state, action) => {
				if (action.payload instanceof Blob) {
					state.synthesis.status = RequestStatus.IDLE;
					const url = window.URL.createObjectURL(action.payload);
					state.synthesis.dataUrl = url;
				} else {
					state.synthesis.status = RequestStatus.FAILED;
				}
			})
			.addCase(synthesizeSpeech.rejected, (state, action: PayloadAction<unknown>) => {
				state.synthesis.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					if (action.payload.response?.data.error && action.payload.response?.data.message) {
						state.synthesis.error = action.payload.response?.data.error;
						state.synthesis.message = action.payload.response?.data.message;
					}
				}
			})
			.addCase(getDictionary.pending, (state) => {
				state.dictionary.status = RequestStatus.LOADING;
			})
			.addCase(getDictionary.fulfilled, (state, action) => {
				state.dictionary.data = action.payload;
				if (Array.isArray(action.payload)) {
					state.dictionary.status = RequestStatus.IDLE;
				} else {
					state.dictionary.status = RequestStatus.FAILED;
				}
			})
			.addCase(getDictionary.rejected, (state) => {
				state.dictionary.status = RequestStatus.FAILED;
			})
			.addCase(exportDictionary.pending, (state) => {
				state.dictionaryExport.status = RequestStatus.LOADING;
			})
			.addCase(exportDictionary.fulfilled, (state, action) => {
				state.dictionaryExport.status = RequestStatus.IDLE;
				if (action.payload && "error" in action.payload) {
					state.dictionaryExport.error = action.payload.error;
					state.dictionaryExport.message = action.payload.message;
				}
			})
			.addCase(exportDictionary.rejected, (state, action: PayloadAction<unknown>) => {
				state.dictionaryExport.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					if (action.payload.response?.data.error && action.payload.response?.data.message) {
						state.dictionaryExport.error = action.payload.response?.data.error;
						state.dictionaryExport.message = action.payload.response?.data.message;
					}
				}
			})
			.addCase(importDictionary.pending, (state) => {
				state.dictionaryImport.status = RequestStatus.LOADING;
			})
			.addCase(importDictionary.fulfilled, (state, action) => {
				state.dictionaryImport.status = RequestStatus.IDLE;
				state.dictionaryImport.error = action.payload.error;
				state.dictionaryImport.message = action.payload.message;
			})
			.addCase(importDictionary.rejected, (state) => {
				state.dictionaryImport.status = RequestStatus.FAILED;
			})
			.addCase(putDictionary.pending, (state) => {
				state.dictionaryPut.status = RequestStatus.LOADING;
			})
			.addCase(putDictionary.fulfilled, (state, action) => {
				state.dictionaryPut.status = RequestStatus.IDLE;
				if (action.payload) {
					state.dictionaryPut.error = action.payload.error;
					state.dictionaryPut.message = action.payload.message;
				}
			})
			.addCase(putDictionary.rejected, (state, action: PayloadAction<unknown>) => {
				state.dictionaryPut.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.dictionaryPut.error = action.payload.response?.data.error;
					state.dictionaryPut.message = action.payload.response?.data.message;
				}
			})
			.addCase(normalizeText.pending, (state) => {
				state.normalization.status = RequestStatus.LOADING;
			})
			.addCase(normalizeText.fulfilled, (state, action) => {
				if (action.payload && "text" in action.payload && action.payload.text) {
					state.normalization.status = RequestStatus.IDLE;
					state.normalization.text = action.payload.text;
				} else state.normalization.status = RequestStatus.FAILED;
			})
			.addCase(normalizeText.rejected, (state, action: PayloadAction<unknown>) => {
				state.normalization.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.normalization.error = action.payload.response?.data.error;
					state.normalization.message = action.payload.response?.data.message;
				}
			})
			.addCase(convertTextToPhonemes.pending, (state) => {
				state.phonemes.status = RequestStatus.LOADING;
			})
			.addCase(convertTextToPhonemes.fulfilled, (state, action) => {
				if (action.payload && "text" in action.payload && action.payload.text) {
					state.phonemes.status = RequestStatus.IDLE;
					state.phonemes.text = action.payload.text;
				} else state.phonemes.status = RequestStatus.FAILED;
			})
			.addCase(convertTextToPhonemes.rejected, (state, action: PayloadAction<unknown>) => {
				state.phonemes.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.phonemes.error = action.payload.response?.data.error;
					state.phonemes.message = action.payload.response?.data.message;
				}
			})
			.addCase(getRevoicersList.pending, (state) => {
				state.revoicersList.status = RequestStatus.LOADING;
			})
			.addCase(getRevoicersList.fulfilled, (state, action) => {
				if (Array.isArray(action.payload)) {
					state.revoicersList.status = RequestStatus.IDLE;
					state.revoicersList.data = action.payload;
				} else state.revoicersList.status = RequestStatus.FAILED;
			})
			.addCase(getRevoicersList.rejected, (state, action: PayloadAction<unknown>) => {
				state.revoicersList.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.revoicersList.error = action.payload.response?.data.error;
					state.revoicersList.message = action.payload.response?.data.message;
				}
			})
			.addCase(revoice.pending, (state) => {
				state.revoice.status = RequestStatus.LOADING;
			})
			.addCase(revoice.fulfilled, (state, action) => {
				if (action.payload instanceof Blob) {
					state.revoice.status = RequestStatus.IDLE;
					const url = window.URL.createObjectURL(action.payload);
					state.revoice.dataUrl = url;
				} else state.revoice.status = RequestStatus.FAILED;
			})
			.addCase(revoice.rejected, (state, action: PayloadAction<unknown>) => {
				state.revoice.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					if (action.payload.response?.data.error && action.payload.response?.data.message) {
						state.revoice.error = action.payload.response?.data.error;
						state.revoice.message = action.payload.response?.data.message;
					}
				}
			});
	},
});

export const {
	editDictionaryDataCell,
	addRow,
	deleteRows,
	clearState,
	clearSynthesisData,
	clearDictionary,
	clearExportDictionary,
	clearImportDictionary,
	clearPutDictionary,
	clearNormalization,
	clearPhonemes,
	clearRevoicersList,
	clearRevoice,
} = ttsSlice.actions;

export const selectSynthesisData = (state: RootState) => state.tts.synthesis;
export const selectDictionary = (state: RootState) => state.tts.dictionary;
export const selectImportDictionaryStatus = (state: RootState) => state.tts.dictionaryImport;
export const selectExportDictionaryStatus = (state: RootState) => state.tts.dictionaryExport;
export const selectPutDictionaryStatus = (state: RootState) => state.tts.dictionaryPut;
export const selectNormalization = (state: RootState) => state.tts.normalization;
export const selectPhonemes = (state: RootState) => state.tts.phonemes;
export const selectRevoicersList = (state: RootState) => state.tts.revoicersList;
export const selectRevoiceData = (state: RootState) => state.tts.revoice;

export default ttsSlice.reducer;
